import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  listInstitutionChangeRequests,
  resolveInstitutionChangeRequest,
  getChangeRequestUserContext,
  type AdminChangeRequest,
} from "@/lib/admin.change-requests.functions";

export const Route = createFileRoute("/admin/change-requests")({
  component: ChangeRequestsPage,
});

type StatusFilter = "pending" | "approved" | "rejected" | "all";

function ChangeRequestsPage() {
  const qc = useQueryClient();
  const listFn = useServerFn(listInstitutionChangeRequests);
  const resolveFn = useServerFn(resolveInstitutionChangeRequest);
  const ctxFn = useServerFn(getChangeRequestUserContext);

  const [status, setStatus] = useState<StatusFilter>("pending");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [note, setNote] = useState("");

  const list = useQuery({
    queryKey: ["admin", "change-requests", status],
    queryFn: () => listFn({ data: { status } }),
  });

  const selected: AdminChangeRequest | undefined = list.data?.requests.find(
    (r) => r.id === selectedId,
  );

  const userCtx = useQuery({
    queryKey: ["admin", "change-requests", "ctx", selected?.user_id ?? null],
    queryFn: () => ctxFn({ data: { userId: selected!.user_id } }),
    enabled: !!selected,
  });

  const resolve = useMutation({
    mutationFn: (v: { id: string; decision: "approve" | "reject" }) =>
      resolveFn({
        data: { id: v.id, decision: v.decision, resolution_note: note.trim() || null },
      }),
    onSuccess: (_, v) => {
      toast.success(v.decision === "approve" ? "Request approved" : "Request rejected");
      setNote("");
      setSelectedId(null);
      qc.invalidateQueries({ queryKey: ["admin", "change-requests"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-6">
      <div>
        <p className="overline mb-2">Institutions</p>
        <h2 className="font-serif text-2xl text-primary mb-1">Change requests</h2>
        <p className="text-sm text-ink-secondary max-w-2xl">
          Users whose 7-day grace window has lapsed must file a request to change
          their main institution. Approving updates the profile, snapshots the prior
          institution to history, and writes an audit entry.
        </p>
      </div>

      <div className="flex gap-2 text-xs">
        {(["pending", "approved", "rejected", "all"] as StatusFilter[]).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => {
              setStatus(s);
              setSelectedId(null);
            }}
            className={`px-3 py-1.5 border border-border uppercase tracking-wide ${
              status === s ? "bg-primary text-primary-foreground" : "hover:bg-muted"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-6">
        <div className="border border-border bg-card">
          {list.isLoading && <p className="p-4 text-sm text-ink-tertiary">Loading…</p>}
          {list.error && (
            <p className="p-4 text-sm text-destructive">
              {list.error instanceof Error ? list.error.message : "Failed to load"}
            </p>
          )}
          {list.data && list.data.requests.length === 0 && (
            <p className="p-6 text-sm text-ink-tertiary text-center">
              No {status === "all" ? "" : status} requests.
            </p>
          )}
          {list.data && list.data.requests.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase tracking-wide text-ink-tertiary">
                <tr>
                  <th className="text-left p-3">User</th>
                  <th className="text-left p-3">From → To</th>
                  <th className="text-left p-3">Submitted</th>
                  <th className="text-left p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {list.data.requests.map((r) => {
                  const target =
                    r.requested_institution_name ?? r.requested_free_text ?? "(unset)";
                  const isSelected = r.id === selectedId;
                  return (
                    <tr
                      key={r.id}
                      onClick={() => setSelectedId(r.id)}
                      className={`border-t border-border cursor-pointer hover:bg-muted/40 ${
                        isSelected ? "bg-muted/60" : ""
                      }`}
                    >
                      <td className="p-3">
                        <div>{r.user_display_name ?? "—"}</div>
                        <div className="text-xs font-mono text-ink-tertiary">
                          {r.user_email ?? r.user_id.slice(0, 8)}
                        </div>
                      </td>
                      <td className="p-3 text-xs">
                        <div className="text-ink-tertiary">
                          {r.current_institution_name ?? "(none)"}
                        </div>
                        <div className="text-ink-primary">→ {target}</div>
                      </td>
                      <td className="p-3 text-xs font-mono text-ink-tertiary">
                        {new Date(r.created_at).toLocaleDateString()}
                      </td>
                      <td className="p-3">
                        <span className="px-2 py-0.5 text-xs font-mono border border-border">
                          {r.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {selected ? (
          <aside className="border border-border bg-card p-5 space-y-5 self-start">
            <div>
              <p className="overline mb-1">Request</p>
              <p className="text-sm">
                <span className="text-ink-tertiary">From:</span>{" "}
                {selected.current_institution_name ?? "(none)"}
              </p>
              <p className="text-sm">
                <span className="text-ink-tertiary">To:</span>{" "}
                {selected.requested_institution_name ?? selected.requested_free_text ?? "(unset)"}
                {selected.requested_free_text && !selected.requested_institution_id && (
                  <span className="ml-2 text-xs text-amber-600">new (free text)</span>
                )}
              </p>
            </div>

            <div>
              <p className="overline mb-1">Reason</p>
              <p className="text-sm whitespace-pre-wrap">{selected.reason}</p>
            </div>

            <div>
              <p className="overline mb-1">User</p>
              {userCtx.isLoading && (
                <p className="text-xs text-ink-tertiary">Loading user…</p>
              )}
              {userCtx.data && (
                <div className="space-y-2 text-sm">
                  <div>
                    {userCtx.data.profile.display_name ?? "—"}{" "}
                    {userCtx.data.profile.flagged && (
                      <span className="ml-1 px-1.5 py-0.5 text-[10px] font-mono bg-destructive text-destructive-foreground">
                        FLAGGED
                      </span>
                    )}
                  </div>
                  <div className="text-xs font-mono text-ink-tertiary">
                    {userCtx.data.profile.email ?? "—"}
                  </div>
                  <div className="text-xs text-ink-tertiary">
                    Joined {new Date(userCtx.data.profile.created_at).toLocaleDateString()} ·{" "}
                    {userCtx.data.recent_reviews.length} recent reviews
                  </div>
                  {userCtx.data.recent_reviews.length > 0 && (
                    <ul className="text-xs space-y-1 mt-2 max-h-32 overflow-auto">
                      {userCtx.data.recent_reviews.map((rv) => (
                        <li key={rv.id} className="text-ink-secondary">
                          <span className="font-mono text-ink-tertiary">
                            {new Date(rv.created_at).toLocaleDateString()}
                          </span>{" "}
                          {rv.title ?? "(untitled)"}{" "}
                          <span className="text-ink-tertiary">— {rv.status}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>

            {selected.status === "pending" ? (
              <div className="space-y-3 pt-2 border-t border-border">
                <label className="block">
                  <span className="overline block mb-1">Resolution note (optional)</span>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={3}
                    maxLength={2000}
                    className="w-full border border-border bg-background px-3 py-2 text-sm"
                    placeholder="Visible in audit log…"
                  />
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => resolve.mutate({ id: selected.id, decision: "approve" })}
                    disabled={resolve.isPending}
                    className="flex-1 bg-primary text-primary-foreground px-4 py-2 text-sm disabled:opacity-50"
                  >
                    {resolve.isPending ? "Working…" : "Approve"}
                  </button>
                  <button
                    type="button"
                    onClick={() => resolve.mutate({ id: selected.id, decision: "reject" })}
                    disabled={resolve.isPending}
                    className="flex-1 border border-destructive text-destructive px-4 py-2 text-sm disabled:opacity-50 hover:bg-destructive hover:text-destructive-foreground transition-colors"
                  >
                    Reject
                  </button>
                </div>
              </div>
            ) : (
              <div className="pt-2 border-t border-border text-sm space-y-1">
                <p className="text-ink-tertiary">
                  Resolved{" "}
                  {selected.resolved_at
                    ? new Date(selected.resolved_at).toLocaleString()
                    : ""}
                </p>
                {selected.resolution_note && (
                  <p className="whitespace-pre-wrap">{selected.resolution_note}</p>
                )}
              </div>
            )}
          </aside>
        ) : (
          <aside className="border border-dashed border-border p-6 text-sm text-ink-tertiary self-start">
            Select a request to view details.
          </aside>
        )}
      </div>
    </div>
  );
}
